import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import WizardStepper, {
  type WizardStep
} from '@components/back-office/shared/wizard-stepper';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { usePublishDialog } from './use-publish-dialog';
import StepChooseConnection from './step-choose-connection';
import StepChooseTarget from './step-choose-target';
import StepChoosePayload from './step-choose-payload';
import StepConfirm from './step-confirm';
import StepInFlight from './step-in-flight';
import type { DialogState } from './types';

export interface PublishDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  documentId: string;
  /** Which kind the opener (editor/workspace) is biased toward. */
  defaultPayloadKind: 'captions' | 'transcript';
  /** Caption languages the document has. */
  availableCaptionLanguages: string[];
  /** When true the user can flip between captions and transcript. */
  allowKindToggle: boolean;
  /** Transcript formats to offer in the payload step. */
  transcriptFormats?: { id: string; label: string }[];
  transcriptTemplates?: { id: string; label: string }[];
  /**
   * Called from the empty state so the user can jump to Connections.
   * Takes an optional connection id so the "Reconnect to upload new
   * videos" alert can tell the destination page which row to open the
   * wizard for.
   */
  onOpenConnectWizard?: (connectionId?: string) => void;
}

const DIALOG_STEP_IDS: Record<DialogState['view'], number> = {
  'choose-connection': 1,
  'choose-target': 2,
  'choose-payload': 3,
  confirm: 4,
  'in-flight': 5
};

export default function PublishDialog({
  isOpen,
  onClose,
  sessionId,
  documentId,
  defaultPayloadKind,
  availableCaptionLanguages,
  allowKindToggle,
  transcriptFormats = [
    { id: 'docx', label: 'DOCX' },
    { id: 'pdf', label: 'PDF' },
    { id: 'txt', label: 'TXT' },
    { id: 'html', label: 'HTML' }
  ],
  transcriptTemplates,
  onOpenConnectWizard
}: PublishDialogProps) {
  const { t } = useTranslation();
  const { isAdmin } = useRoles();
  const {
    state,
    connections,
    connectionsLoading,
    selectedConnection,
    youtubeUploadsEnabled,
    selectedConnectionHasUploadScope,
    publicPrivacyAvailable,
    targets,
    targetsLoading,
    targetsHasNextPage,
    targetsIsFetchingNextPage,
    targetsFetchNextPage,
    targetQuery,
    setTargetQuery,
    priorAttempt,
    canAdvance,
    chooseConnection,
    pickTarget,
    setPayload,
    confirmPublish,
    retryPublish,
    pickDifferentTarget,
    cancelActiveJob,
    isCancellingJob,
    goTo
  } = usePublishDialog({
    isOpen,
    sessionId,
    documentId,
    defaultPayloadKind,
    availableCaptionLanguages
  });

  const steps: WizardStep[] = useMemo(
    () => [
      { id: 1, label: t('publish-step-choose-connection') },
      { id: 2, label: t('publish-step-choose-target') },
      { id: 3, label: t('publish-step-choose-payload') },
      { id: 4, label: t('publish-step-confirm') },
      { id: 5, label: t('publish-step-progress') }
    ],
    [t]
  );

  const currentStepId = DIALOG_STEP_IDS[state.view];

  const handleBack = () => {
    const order: DialogState['view'][] = [
      'choose-connection',
      'choose-target',
      'choose-payload',
      'confirm'
    ];
    const i = order.indexOf(state.view);
    if (i > 0) goTo(order[i - 1]);
  };

  const handleNext = async () => {
    switch (state.view) {
      case 'choose-connection':
        goTo('choose-target');
        return;
      case 'choose-target':
        goTo('choose-payload');
        return;
      case 'choose-payload':
        goTo('confirm');
        return;
      case 'confirm':
        await confirmPublish();
        return;
      default:
        return;
    }
  };

  const agendaItemTitle =
    state.target?.kind === 'ibabs_meeting'
      ? state.target.agenda_items.find(a => a.id === state.agendaItemId)?.title
      : undefined;

  const primaryLabel = (() => {
    if (state.view === 'confirm')
      return priorAttempt
        ? t('publish-primary-replace')
        : t('publish-primary-send');
    return t('next');
  })();

  const renderBody = () => {
    switch (state.view) {
      case 'choose-connection':
        return (
          <StepChooseConnection
            connections={connections}
            isLoading={connectionsLoading}
            value={state.connectionId}
            onChange={chooseConnection}
            onOpenConnectWizard={onOpenConnectWizard}
            canManageConnections={!!isAdmin}
          />
        );
      case 'choose-target':
        return (
          <StepChooseTarget
            platform={selectedConnection?.platform ?? 'youtube'}
            allowNewVideo={youtubeUploadsEnabled}
            connectionHasUploadScope={selectedConnectionHasUploadScope}
            publicPrivacyAvailable={publicPrivacyAvailable}
            onReconnectForUpload={() => {
              if (!selectedConnection) return;
              onOpenConnectWizard?.(selectedConnection.id);
              onClose();
            }}
            targets={targets}
            isLoading={targetsLoading}
            query={targetQuery}
            onQueryChange={setTargetQuery}
            selectedId={state.target?.id ?? null}
            agendaItemId={state.agendaItemId}
            selectedTarget={state.target}
            onPick={pickTarget}
            hasNextPage={targetsHasNextPage}
            isFetchingNextPage={targetsIsFetchingNextPage}
            fetchNextPage={() => void targetsFetchNextPage()}
          />
        );
      case 'choose-payload':
        return (
          <StepChoosePayload
            value={state.payload}
            onChange={setPayload}
            availableLanguages={availableCaptionLanguages}
            transcriptFormats={transcriptFormats}
            transcriptTemplates={transcriptTemplates}
            allowKindToggle={allowKindToggle}
          />
        );
      case 'confirm':
        if (!selectedConnection || !state.target) return null;
        return (
          <StepConfirm
            connection={selectedConnection}
            target={state.target}
            agendaItemTitle={agendaItemTitle}
            payload={state.payload}
            priorAttempt={priorAttempt}
          />
        );
      case 'in-flight':
        if (!state.activeJob) return null;
        return (
          <StepInFlight
            job={state.activeJob}
            onRetry={retryPublish}
            onReconnect={() => {
              onOpenConnectWizard?.();
              onClose();
            }}
            onPickDifferentTarget={pickDifferentTarget}
            onDone={onClose}
            onCancel={cancelActiveJob}
            isCancelling={isCancellingJob}
          />
        );
      default:
        return null;
    }
  };

  const isFlight = state.view === 'in-flight';
  const canShowBack = currentStepId > 1 && !isFlight;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title={t('publish-dialog-title')}
      caption={t('publish-dialog-subtitle')}
      footerActions={
        isFlight
          ? []
          : [
              ...(canShowBack
                ? [
                    <Button
                      key="back"
                      onClick={handleBack}
                      label={t('back')}
                      size="small"
                      variant="smoke"
                      icon={
                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      }
                    />
                  ]
                : []),
              <Button
                key="next"
                onClick={handleNext}
                label={primaryLabel}
                size="small"
                variant="primary"
                icon={
                  state.view === 'confirm' ? undefined : (
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  )
                }
                iconPosition="right"
                disabled={!canAdvance}
              />
            ]
      }
    >
      <div className="pb-2 border-b border-hairline">
        <WizardStepper steps={steps} currentStep={currentStepId} />
      </div>
      <div className="max-h-[55vh] overflow-y-auto px-1 pt-2 pb-1">
        {renderBody()}
      </div>
    </Modal>
  );
}
